import { useCallback, useEffect, useState } from 'react';

/**
 * 把「现在在哪一屏」放进地址栏。
 *
 * ★ 在这之前刷新一下就回首页 —— 而刷新是最常发生的事:
 *   改完设置刷一下看看生效没有、网络卡了刷一下、手机切回来自动重载。
 *   每一次都被扔回概览页,然后重新点三下找回原来那一屏。
 *
 * ★ 用 hash 而不是 history:静态托管(Cloudflare Pages)下
 *   `/orders` 这种路径要靠服务端回退到 index.html 才不 404,
 *   而 hash 由浏览器自己处理,**没有一个环节会配错**。
 *   代价是地址长成 `/#/orders` —— 换来的是它在任何托管上都成立。
 *
 * ★ 顺带白拿两样:浏览器的前进/后退能用了,
 *   而且「把这一屏发给同事」变成了可能。
 */
/*
 * ★ 位置必须保住,**不能把空段挤掉**。
 *
 *   一开始地址栏是空的,而二级标签(第 1 段)可能先被写 ——
 *   这时候如果把空的第 0 段过滤掉,`DELIVERING` 就落到了第 0 段上,
 *   于是「当前在哪一屏」变成了一个不存在的值,**整屏空白**。
 *   (第一版就是这样,而症状是刷新之后 main 里什么都没有。)
 */
export function currentRoute(): string[] {
  const raw = window.location.hash.replace(/^#\/?/, '');
  if (raw === '') return [];
  // 只去掉结尾的空段;中间的空位是有意义的占位
  const parts = raw.split('/');
  while (parts.length > 0 && parts[parts.length - 1] === '') parts.pop();
  return parts;
}

export function setRoute(parts: string[]): void {
  const trimmed = [...parts];
  while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') trimmed.pop();
  const next = `#/${trimmed.join('/')}`;
  if (window.location.hash !== next) window.location.hash = next;
}

/**
 * 读写地址栏里的第 n 段。
 *
 * @param index 第几段(0 = 一级导航,1 = 二级标签)
 * @param fallback 地址栏里没有时用什么
 */
export function useRouteSegment(
  index: number,
  fallback: string,
): [string, (v: string) => void] {
  const read = useCallback(() => currentRoute()[index] || fallback, [index, fallback]);
  const [value, setValue] = useState(read);

  /*
   * ★ 一进来就把自己那一段写进地址栏。
   *   不写的话地址栏是空的,而「把这一屏发给同事」拿到的是首页;
   *   更要紧的是二级标签写进去时前面会缺一段(见上面 currentRoute 的说明)。
   */
  useEffect(() => {
    const parts = currentRoute();
    if (!parts[index]) {
      while (parts.length < index) parts.push('');
      parts[index] = read();
      setRoute(parts);
    }
    // 只在挂载时做一次 —— 之后由用户的点击驱动
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    /*
     * ★ 监听 hashchange,不只是自己 setState ——
     *   否则浏览器的后退按钮点下去地址变了、界面没变,
     *   而那种「地址和内容对不上」比没有路由更让人困惑。
     */
    const onHash = () => setValue(read());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [read]);

  const update = useCallback(
    (v: string) => {
      const parts = currentRoute();
      // 补齐前面的段,否则 parts[1] 会写到空数组的第 0 位上
      while (parts.length < index) parts.push('');
      parts[index] = v;
      /*
       * ★ 改一级导航时把后面的段丢掉:
       *   从「设置 / 对象存储」切到「订单」,地址不该留着
       *   `#/orders/storage` —— 那个 storage 属于上一屏。
       */
      setRoute(parts.slice(0, index + 1));
      setValue(v);
    },
    [index],
  );

  return [value, update];
}
